import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useMagazineDetail, getTag, getTags, formatFileSize } from '@/hooks/useMagazines';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, BookOpen, Calendar, Download, ExternalLink, Globe } from 'lucide-react';

const MY_PUBKEY = 'a8d1560d6a647d501699167246f237b36fb123f89168fda11dc743533fec7a08';

const MagazinePage = () => {
  const { dTag } = useParams<{ dTag: string }>();
  const { data, isLoading } = useMagazineDetail(MY_PUBKEY, dTag);

  const magazine = data?.magazine;
  const issues = data?.issues ?? [];

  const title = magazine ? getTag(magazine, 'title') : null;
  const image = magazine ? getTag(magazine, 'image') : null;
  const summary = magazine ? (getTag(magazine, 'summary') || magazine.content || null) : null;
  const websiteUrl = magazine ? getTag(magazine, 'url') : null;
  const lang = magazine ? getTag(magazine, 'l') : null;
  const tags = magazine ? getTags(magazine, 't') : [];

  useSeoMeta({
    title: title ?? 'Magazine',
    description: summary ?? 'Magazine on stl1988\'s homepage',
    ogTitle: title ?? 'Magazine',
    ogDescription: summary ?? undefined,
    ogImage: image ?? 'https://stl1988.shakespeare.wtf/og-image.jpg',
    ogType: 'website',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container py-8 px-4 max-w-5xl">
          <Skeleton className="h-9 w-24 mb-6" />
          <div className="flex flex-col sm:flex-row gap-8">
            <Skeleton className="w-full sm:w-48 aspect-[2/3] rounded-xl flex-shrink-0" />
            <div className="flex-1 space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!magazine) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container py-8 px-4 max-w-5xl">
          <Button asChild variant="ghost" size="sm" className="mb-6">
            <Link to="/"><ArrowLeft className="h-4 w-4 mr-2" />Back</Link>
          </Button>
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Magazine not found.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container py-8 px-4 max-w-5xl">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/"><ArrowLeft className="h-4 w-4 mr-2" />Back</Link>
        </Button>

        {/* Magazine header */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 mb-10">
          <div className="w-36 sm:w-48 aspect-[2/3] flex-shrink-0 rounded-xl overflow-hidden shadow-lg self-start mx-auto sm:mx-0">
            {image ? (
              <img src={image} alt={title ?? 'Magazine'} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <BookOpen className="h-16 w-16 text-primary/40" />
              </div>
            )}
          </div>

          <div className="flex-1 space-y-3">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">{title}</h1>

            {summary && (
              <p className="text-muted-foreground leading-relaxed">{summary}</p>
            )}

            <div className="flex flex-wrap gap-2 items-center">
              {lang && (
                <Badge variant="outline" className="gap-1">
                  <Globe className="h-3 w-3" />
                  {lang.toUpperCase()}
                </Badge>
              )}
              {tags.map((tag, i) => (
                <Badge key={i} variant="secondary">{tag}</Badge>
              ))}
            </div>

            {websiteUrl && (
              <Button asChild variant="outline" size="sm" className="gap-2">
                <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                  <Globe className="h-4 w-4" />
                  Website
                  <ExternalLink className="h-3 w-3" />
                </a>
              </Button>
            )}

            <p className="text-sm text-muted-foreground">
              {issues.length} {issues.length === 1 ? 'Issue' : 'Issues'}
            </p>
          </div>
        </div>

        <Separator className="mb-8" />

        {/* Issues */}
        <h2 className="font-heading text-2xl font-bold mb-6">Issues</h2>

        {issues.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">Keine Ausgaben gefunden.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {issues.map((issue) => (
              <IssueCard key={issue.id} issue={issue} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function IssueCard({ issue }: { issue: NostrEvent }) {
  const title = getTag(issue, 'title');
  const issueLabel = getTag(issue, 'issue');
  const image = getTag(issue, 'image');
  const url = getTag(issue, 'url');
  const summary = getTag(issue, 'summary') || issue.content || null;
  const publishedAt = getTag(issue, 'published_at');
  const mime = getTag(issue, 'm') ?? 'application/pdf';
  const size = getTag(issue, 'size');

  const isEpub = mime === 'application/epub+zip';
  const ext = isEpub ? 'EPUB' : 'PDF';
  const fileSize = formatFileSize(size);

  const dateStr = publishedAt
    ? new Date(parseInt(publishedAt) * 1000).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
      })
    : new Date(issue.created_at * 1000).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
      });

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 group flex flex-col">
      <div className="relative overflow-hidden bg-muted aspect-[2/3] flex-shrink-0">
        {image ? (
          <img
            src={image}
            alt={title ?? 'Issue'}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex flex-col items-center justify-center gap-2 p-2">
            <BookOpen className="h-8 w-8 text-primary/50" />
            {issueLabel && (
              <span className="text-xs text-center text-muted-foreground">{issueLabel}</span>
            )}
          </div>
        )}
        <div className="absolute top-1.5 left-1.5">
          <Badge className="text-xs bg-background/80 text-foreground border px-1.5 py-0">
            {ext}
          </Badge>
        </div>
      </div>

      <CardContent className="p-2 flex-1">
        <p className="text-xs font-semibold leading-tight group-hover:text-primary transition-colors line-clamp-2">
          {issueLabel ?? title ?? 'Issue'}
        </p>
        <div className="flex items-center gap-1 mt-1">
          <Calendar className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
          <p className="text-xs text-muted-foreground truncate">{dateStr}</p>
        </div>
        {fileSize && (
          <p className="text-xs text-muted-foreground mt-0.5">{fileSize}</p>
        )}
        {summary && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{summary}</p>
        )}
      </CardContent>

      {url && (
        <div className="px-2 pb-2">
          <Button asChild size="sm" variant="outline" className="w-full h-7 gap-1 text-xs">
            <a href={url} target="_blank" rel="noopener noreferrer" download>
              <Download className="h-3 w-3" />
              Download
            </a>
          </Button>
        </div>
      )}
    </Card>
  );
}

export default MagazinePage;
